import { PaymentsProcessor } from '@weave/schema-gen-ts/dist/schemas/payments-platform/payments-config/payments_config.pb';
import { Trans, useTranslation } from '@frontend/i18n';
import { PaymentsFeatureFlags, useStripeDashboardLink, useSelectedLocation } from '@frontend/payments-hooks';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { BannerNotification, Text, TextLink } from '@frontend/design-system';
import { PaymentSettingsCard } from '../../atoms/payments-card';
import { JustifiStatus } from './justifi-status/justifi-status';
import { StripeStatus } from './stripe-status/stripe-status';

type MerchantProps = {
  processors?: Record<string, PaymentsProcessor>;
  isInMultiLocationSetup?: boolean;
};
export const Merchant = ({ processors, isInMultiLocationSetup }: MerchantProps) => {
  const { t } = useTranslation('payments');
  const locationId = useSelectedLocation();
  const { getFeatureFlagValue } = useAppFlagStore();

  const { handleOpenStripeDashboard, isGettingDashboardLink } = useStripeDashboardLink({
    locationId,
  });

  if (!locationId) {
    console.error('Merchant: locationId is not defined');
    return null;
  }

  const processor = processors?.[locationId];

  const isStripe =
    !processor ||
    processor === PaymentsProcessor.PAYMENTS_PROCESSOR_STRIPE ||
    processor === PaymentsProcessor.PAYMENTS_PROCESSOR_UNKNOWN;
  const isJustiFi = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.EnableJustifyOnboarding,
    locationIds: [locationId],
  });
  const isJustiFiProcessor = processor === PaymentsProcessor.PAYMENTS_PROCESSOR_JUSTIFI;

  // Single-location view
  if (!isInMultiLocationSetup) {
    // When both Stripe and JustiFi are available, show them in separate cards
    if (isStripe && isJustiFi) {
      return (
        <>
          <PaymentSettingsCard locationId={locationId}>
            <StripeStatus locationId={locationId} />
          </PaymentSettingsCard>
          <PaymentSettingsCard locationId={locationId}>
            <JustifiStatus isActive={isJustiFiProcessor} />
          </PaymentSettingsCard>
        </>
      );
    }

    // Otherwise show it in a single card coz only one processor is available
    return (
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <PaymentSettingsCard locationId={locationId}>
          {isStripe && <StripeStatus locationId={locationId} />}
          {isJustiFi && <JustifiStatus isActive={isJustiFiProcessor} />}
        </PaymentSettingsCard>
        {isJustiFiProcessor && (
          <BannerNotification
            css={{
              minWidth: theme.spacing(90), // same as the card
            }}
            status='info'
            message={
              <Trans t={t}>
                <Text>
                  Looking for your Stripe account? You can still access it in your{' '}
                  {isGettingDashboardLink ? (
                    <TextLink disabled>{t('Opening Stripe Dashboard...')}</TextLink>
                  ) : (
                    <TextLink onClick={handleOpenStripeDashboard}>Stripe Dashboard</TextLink>
                  )}
                  .
                </Text>
              </Trans>
            }
          />
        )}
      </div>
    );
  }

  // Multi-location view
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
      }}
    >
      {/* Show Stripe status if applicable */}
      {isStripe && <StripeStatus locationId={locationId} />}

      {/* Add separator between Stripe and JustiFi */}
      {isStripe && isJustiFi && <hr />}

      {/* Show JustiFi status if enabled */}
      {isJustiFi && <JustifiStatus isActive={isJustiFiProcessor} />}
    </div>
  );
};
